"use client";

import { track, useSectionView } from "@/app/utils/analytics";
import Image from "next/image";
import { useState } from "react";

export default function TabFeatures() {
  const [option, setOption] = useState("Payments");
  const sectionRef = useSectionView<HTMLDivElement>("features");

  const checkmark = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="lucide lucide-check-icon lucide-check text-primary shrink-0"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );

  const paymentsScreen = (
    <div className="card-body p-10">
      <h2 className="card-title text-white text-3xl font-semibold">Payments</h2>
      <p className="card-title text-zinc-400 text-xl mb-2">
        Monetize your Chrome extension with an easy-to-implement payment system.
      </p>
      <div className="flex flex-col gap-y-6">
        <div className="flex flex-col gap-y-2 text-lg text-zinc-300">
          <div className="flex gap-x-2">
            {checkmark}
            <p>Handle webhooks to update the user's account</p>
          </div>
          <div className="flex gap-x-2">
            {checkmark}
            <p>Supports subscriptions and one-time purchases</p>
          </div>
          <div className="flex text-green-400 gap-x-2 font-semibold mt-1">
            {checkmark}
            <p>Time saved: 6 hours</p>
          </div>

          <div className="flex gap-x-2 items-center mt-5">
            <Image
              className="w-8"
              src="/images/general/stripe-logo.webp"
              width={100}
              height={100}
              alt="Logo of the technology Stripe"
            />
            <p>
              with{" "}
              <a
                href="https://stripe.com/"
                target="_blank"
                className="underline cursor-pointer text-zinc-400 hover:text-white"
              >
                Stripe
              </a>
            </p>
          </div>
        </div>
        <div className="self-center shadow-lg shadow-black/20 border border-line bg-ink-deep lg:p-2 backdrop-blur-lg rounded-2xl">
          <Image
            className="w-full rounded-xl"
            src="/images/general/feature-payments.webp"
            width={800}
            height={500}
            alt="Payment example inside of ExtensionFast"
          />
        </div>
      </div>
    </div>
  );

  const loginScreen = (
    <div className="card-body p-10">
      <h2 className="card-title text-white text-3xl font-semibold">Login</h2>
      <p className="card-title text-zinc-400 text-xl mb-2">
        Have authentication handled for you, no need to stress how to secure
        your users.
      </p>
      <div className="flex flex-col gap-y-6">
        <div className="flex flex-col gap-y-2 text-lg text-zinc-300">
          <div className="flex gap-x-2">
            {checkmark}
            <p>Email and password login</p>
          </div>
          <div className="flex gap-x-2">
            {checkmark}
            <p>One-click Google sign-in with oAuth</p>
          </div>
          <div className="flex text-green-400 gap-x-2 font-semibold mt-1">
            {checkmark}
            <p>Time saved: 1 day</p>
          </div>

          <div className="flex gap-x-4 items-center mt-5">
            <div className="flex gap-x-2">
              <Image
                className="w-8"
                src="/images/general/firebase-logo.webp"
                width={100}
                height={100}
                alt="Logo of the technology Firebase"
              />
              <p>
                with{" "}
                <a
                  href="https://firebase.google.com/"
                  target="_blank"
                  className="underline cursor-pointer text-zinc-400 hover:text-white"
                >
                  Firebase
                </a>
              </p>
            </div>
            <div className="font-bold">
              <p>OR</p>
            </div>
            <div className="flex gap-x-2">
              <Image
                className="w-8"
                src="/images/general/supabase-logo.webp"
                width={100}
                height={100}
                alt="Logo of the technology Supabase"
              />
              <p>
                with{" "}
                <a
                  href="https://supabase.com/"
                  target="_blank"
                  className="underline cursor-pointer text-zinc-400 hover:text-white"
                >
                  Supabase
                </a>
              </p>
            </div>
          </div>
        </div>
        <div className="self-center shadow-lg shadow-black/20 border border-line bg-ink-deep lg:p-2 backdrop-blur-lg rounded-2xl">
          <Image
            className="w-full rounded-xl"
            src="/images/general/feature-login.webp"
            width={800}
            height={500}
            alt="Different login pages for ExtensionFast"
          />
        </div>
      </div>
    </div>
  );

  const styleScreen = (
    <div className="card-body p-10">
      <h2 className="card-title text-white text-3xl font-semibold">Style</h2>
      <p className="card-title text-zinc-400 text-xl mb-2">
        Easily handle styling inside of your Chrome extension with pre-built
        components and quick CSS.
      </p>
      <div className="flex flex-col gap-y-6">
        <div className="flex flex-col gap-y-2 text-lg text-zinc-300">
          <div className="flex gap-x-2">
            {checkmark}
            <p>Implement pre-built components to skip the busy work</p>
          </div>
          <div className="flex gap-x-2">
            {checkmark}
            <p>Consistent styling across the platform</p>
          </div>
          <div className="flex text-green-400 gap-x-2 font-semibold mt-1">
            {checkmark}
            <p>Time saved: 4 hours</p>
          </div>

          <div className="flex gap-x-2 items-center mt-5">
            <Image
              className="w-8"
              src="/images/general/daisyui-logo.webp"
              width={100}
              height={100}
              alt="Logo of the technology DaisyUI"
            />
            <p>
              with{" "}
              <a
                href="https://daisyui.com/"
                target="_blank"
                className="underline cursor-pointer text-zinc-400 hover:text-white"
              >
                DaisyUI
              </a>
            </p>
          </div>
          <div className="flex gap-x-2 items-center mt-5">
            <Image
              className="w-8"
              src="/images/general/tailwind-logo.webp"
              width={100}
              height={100}
              alt="Logo of the technology TailwindCSS"
            />
            <p>
              with{" "}
              <a
                href="https://tailwindcss.com/"
                target="_blank"
                className="underline cursor-pointer text-zinc-400 hover:text-white"
              >
                Tailwind CSS
              </a>
            </p>
          </div>
        </div>
        <div className="self-center shadow-lg shadow-black/20 border border-line bg-ink-deep lg:p-2 backdrop-blur-lg rounded-2xl">
          <Image
            className="w-full rounded-xl"
            src="/images/general/feature-styling.webp"
            width={800}
            height={500}
            alt="Showcase of DaisyUI components for styling"
          />
        </div>
      </div>
    </div>
  );

  const databaseScreen = (
    <div className="card-body p-10">
      <h2 className="card-title text-white text-3xl font-semibold">Database</h2>
      <p className="card-title text-zinc-400 text-xl mb-2">
        Effortlessly manage user data and payment information.
      </p>
      <div className="flex flex-col gap-y-6">
        <div className="flex flex-col gap-y-2 text-lg text-zinc-300">
          <div className="flex gap-x-2">
            {checkmark}
            <p>Firebase/Supabase schema to make your life easier</p>
          </div>
          <div className="flex gap-x-2">
            {checkmark}
            <p>Handle user data with Firebase/Supabase, no sweat</p>
          </div>
          <div className="flex text-green-400 gap-x-2 font-semibold mt-1">
            {checkmark}
            <p>Time saved: 6 hours</p>
          </div>
          <div className="flex gap-x-4 items-center mt-5">
            <div className="flex gap-x-2">
              <Image
                className="w-8"
                src="/images/general/firebase-logo.webp"
                width={100}
                height={100}
                alt="Logo of the technology Firebase"
              />
              <p>
                with{" "}
                <a
                  href="https://firebase.google.com/"
                  target="_blank"
                  className="underline cursor-pointer text-zinc-400 hover:text-white"
                >
                  Firebase
                </a>
              </p>
            </div>
            <div className="font-bold">
              <p>OR</p>
            </div>
            <div className="flex gap-x-2">
              <Image
                className="w-8"
                src="/images/general/supabase-logo.webp"
                width={100}
                height={100}
                alt="Logo of the technology Supabase"
              />
              <p>
                with{" "}
                <a
                  href="https://supabase.com/"
                  target="_blank"
                  className="underline cursor-pointer text-zinc-400 hover:text-white"
                >
                  Supabase
                </a>
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-col justify-center items-center gap-y-6">
          <div className="self-center shadow-lg shadow-black/20 border border-line bg-ink-deep lg:p-2 backdrop-blur-lg rounded-2xl">
            <Image
              className="w-full rounded-xl"
              src="/images/general/feature-database.webp"
              width={800}
              height={500}
              alt="Firebase database fields"
            />
          </div>
          <div className="self-center shadow-lg shadow-black/20 border border-line bg-ink-deep lg:p-2 backdrop-blur-lg rounded-2xl">
            <Image
              className="w-full rounded-xl"
              src="/images/general/supabase-database.webp"
              width={800}
              height={500}
              alt="Supabase database schema"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const submissionScreen = (
    <div className="card-body p-10">
      <h2 className="card-title text-white text-3xl font-semibold">
        Submission
      </h2>
      <p className="card-title text-zinc-400 text-xl mb-2">
        Submit your app with confidence and ease. No guesswork, no roadblocks.
      </p>
      <div className="flex flex-col gap-y-6">
        <div className="flex flex-col gap-y-2 text-lg text-zinc-300">
          <div className="flex gap-x-2">
            {checkmark}
            <p>Step-by-step store submission guides</p>
          </div>
          <div className="flex gap-x-2">
            {checkmark}
            <p>Pre-built store asset templates</p>
          </div>
          <div className="flex gap-x-2">
            {checkmark}
            <p>Chrome Web Store compliant</p>
          </div>
          <div className="flex text-green-400 gap-x-2 font-semibold mt-1">
            {checkmark}
            <p>Time saved: 2 days</p>
          </div>

          <div className="flex gap-x-2 items-center mt-5">
            <Image
              className="w-8"
              src="/images/general/chrome-logo.webp"
              width={100}
              height={100}
              alt="Logo of the Chrome browser"
            />
            <p>
              with{" "}
              <a
                href="https://developer.chrome.com/docs/webstore"
                target="_blank"
                className="underline cursor-pointer text-zinc-400 hover:text-white"
              >
                Chrome Web Store
              </a>
            </p>
          </div>
        </div>
        <div className="flex flex-col justify-center items-center gap-y-6">
          <div className="self-center shadow-lg shadow-black/20 border border-line bg-ink-deep lg:p-2 backdrop-blur-lg rounded-2xl">
            <Image
              className="w-full rounded-xl"
              src="/images/general/feature-submission-guide.webp"
              width={800}
              height={500}
              alt="Different login pages for ExtensionFast"
            />
          </div>
          <div className="self-center shadow-lg shadow-black/20 border border-line bg-ink-deep lg:p-2 backdrop-blur-lg rounded-2xl">
            <Image
              className="w-full rounded-xl"
              src="/images/general/feature-submission-asset.webp"
              width={800}
              height={500}
              alt="Different login pages for ExtensionFast"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const moreScreen = (
    <div className="card-body p-10">
      <h2 className="card-title text-white text-3xl font-semibold">
        Added Benefits
      </h2>
      <p className="card-title text-zinc-400 text-xl mb-2">
        In addition to the core benefits of using ExtensionFast, here are some
        added ones.
      </p>
      <div className="flex flex-col gap-y-2 text-lg text-zinc-300">
        <div className="flex gap-x-2">
          {checkmark}
          <p>Extensive documentation with video guides</p>
        </div>
        <div className="flex gap-x-2">
          {checkmark}
          <p>1-on-1 support if you ever get stuck</p>
        </div>
        <div className="flex gap-x-2">
          {checkmark}
          <p>Single click deployment with WXT</p>
        </div>
        <div className="flex gap-x-2">
          {checkmark}
          <p>Lifetime codebase updates to stay up-to-speed</p>
        </div>
        <div className="flex text-green-400 gap-x-2 font-semibold">
          {checkmark}
          <p>Total time saved: ∞ hours</p>
        </div>
      </div>
    </div>
  );

  return (
    <div
      ref={sectionRef}
      className={`bg-ink flex flex-col justify-center items-center w-full px-6 py-24`}
      id="features"
    >
      <div className="flex flex-col justify-center items-center w-full xl:w-[1250px] mx-auto">
        <div className="bg-line-grid relative overflow-hidden rounded-3xl border border-line bg-ink-soft w-full h-full lg:h-5/6 py-14 px-6 flex flex-col justify-center items-center">
          <div className="pointer-events-none absolute left-1/2 top-0 h-72 w-[700px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(11,99,222,0.1),transparent_70%)] blur-3xl" />
          <div
            className={`mb-6 inline-flex items-center gap-2 rounded-full border border-line bg-panel/70 px-4 py-1.5 text-sm font-medium text-zinc-300 shadow-sm backdrop-blur`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-primary"
            >
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
              <path d="M12 5 9.04 7.96a2.17 2.17 0 0 0 0 3.08c.82.82 2.13.85 3 .07l2.07-1.9a2.82 2.82 0 0 1 3.79 0l2.96 2.66" />
              <path d="m18 15-2-2" />
              <path d="m15 18-2-2" />
            </svg>
            What we offer
          </div>
          <h2 className="text-4xl md:text-5xl font-medium text-white tracking-tight text-center max-w-full mx-auto">
            Everything you need to{" "}
            <span className="text-primary">ship a paid extension</span>
          </h2>
          <div className="relative flex flex-col justify-center items-center mt-10">
            <div className="flex flex-wrap justify-center items-center bg-ink-deep/80 border border-line shadow-lg shadow-black/30 rounded-full p-1.5 mb-10 min-w-80 gap-1 backdrop-blur">
              {[
                {
                  key: "Payments",
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect width="20" height="14" x="2" y="5" rx="2" />
                      <line x1="2" x2="22" y1="10" y2="10" />
                    </svg>
                  ),
                },
                {
                  key: "Login",
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 20a6 6 0 0 0-12 0" />
                      <circle cx="12" cy="10" r="4" />
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                  ),
                },
                {
                  key: "Style",
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m9.06 11.9 8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08" />
                      <path d="M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-3-3.02z" />
                    </svg>
                  ),
                },
                {
                  key: "Database",
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <ellipse cx="12" cy="5" rx="9" ry="3" />
                      <path d="M3 5V19A9 3 0 0 0 21 19V5" />
                      <path d="M3 12A9 3 0 0 0 21 12" />
                    </svg>
                  ),
                },
                {
                  key: "Submission",
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 13v8" />
                      <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
                      <path d="m8 17 4-4 4 4" />
                    </svg>
                  ),
                },
                {
                  key: "More",
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M17 12h.01" />
                      <path d="M12 12h.01" />
                      <path d="M7 12h.01" />
                    </svg>
                  ),
                },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={(e) => {
                    setOption(tab.key);
                    track("feature_tab_viewed", { tab: tab.key });
                    e.currentTarget.blur();
                  }}
                  className={`flex items-center gap-x-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    option === tab.key
                      ? "bg-primary text-white ring-1 ring-inset ring-white/15 shadow-[0_6px_20px_-8px_rgba(11,99,222,0.7)]"
                      : "text-zinc-400 hover:bg-panel-light hover:text-white"
                  }`}
                >
                  {tab.icon}
                  <span>{tab.key}</span>
                </button>
              ))}
            </div>
            <div className="card flex bg-panel min-w-80 max-w-5xl w-full shadow-2xl shadow-black/30 border border-line rounded-2xl overflow-hidden">
              <div className="w-full">
                  {option === "Payments" && paymentsScreen}
                  {option === "Login" && loginScreen}
                  {option === "Style" && styleScreen}
                  {option === "Database" && databaseScreen}
                  {option === "Submission" && submissionScreen}
                  {option === "More" && moreScreen}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
